// Ported from ionq-cloud-ui's RowActionPopover + JobsRowActionPopover.
// One deliberate reduction: no "Cancel job" action — canceling drives the
// job lifecycle (Pub/Sub → workers) and must go through cloud-job-manager,
// which the SQL-only BFF doesn't front yet. Download / view / copy carry
// over unchanged.

import {
    Box,
    Button,
    Flex,
    Icon,
    IconButton,
    Popover,
    Portal,
    Text,
    useDisclosure,
} from '@chakra-ui/react';
import { toaster } from '@ionq/legacy-ui';
import React from 'react';
import type { IconType } from 'react-icons';
import { MdMoreHoriz, MdOutlineFileDownload } from 'react-icons/md';
import { downloadTextFile } from '../../lib/download';

interface RowActionMenuItem {
    key: string;
    node: React.ReactNode;
    /** Items without a handler just close the menu (rows are inert). */
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => unknown;
}

const RowActionPopover = ({
    ariaLabel,
    menuItems,
    onClickEvent = () => {},
    icon = null,
}: {
    ariaLabel?: string;
    menuItems: RowActionMenuItem[];
    onClickEvent?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    icon?: IconType | null;
}) => {
    const { open: isOpen, onClose, onOpen } = useDisclosure();
    const [onTopHover, setOnTopHover] = React.useState(false);
    const [onBottomHover, setOnBottomHover] = React.useState(false);
    const hasIcon = icon !== null;

    const isHovering = !isOpen && hasIcon && (onTopHover || onBottomHover);

    const trigger = (
        <Flex>
            {hasIcon && isHovering && (
                <IconButton
                    height='26px'
                    maxHeight='26px'
                    width='26px'
                    maxWidth='26px'
                    position='absolute'
                    size='xs'
                    fontSize='16px'
                    backgroundColor='bg.panel'
                    border='1px solid'
                    borderColor='border'
                    ml='-26px'
                    color='fg.muted'
                    borderRadius='5px 0 0 5px'
                    borderRight='none'
                    boxShadow='none'
                    aria-label={ariaLabel ?? 'Row action'}
                    cursor='pointer'
                    _hover={{
                        backgroundColor: 'bg.hover',
                        color: 'fg',
                    }}
                    _active={{ color: 'fg' }}
                    onClick={(e) => {
                        onClickEvent(e);
                        setOnBottomHover(false);
                        setOnTopHover(false);
                    }}
                    onMouseEnter={() => {
                        setOnTopHover(true);
                        onClose();
                    }}
                    onMouseLeave={() => setOnTopHover(false)}
                >
                    <Icon as={icon} />
                </IconButton>
            )}
            <IconButton
                height='26px'
                maxHeight='26px'
                width='26px'
                maxWidth='26px'
                size='xs'
                backgroundColor='bg.panel'
                _hover={{
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: 'border',
                    bg: 'bg.hover',
                    color: 'fg',
                }}
                border={isHovering ? '1px solid' : undefined}
                borderColor='border'
                borderRadius={hasIcon && isHovering ? '0 5px 5px 0' : '5px'}
                onMouseEnter={() => setOnBottomHover(true)}
                onMouseLeave={() => setOnBottomHover(false)}
                boxShadow='none'
                color='fg.muted'
                _active={{ color: 'fg' }}
                aria-label='Row actions'
                onClick={(e) => {
                    e.stopPropagation();
                    if (isOpen) onClose();
                    else onOpen();
                }}
            >
                <Icon as={MdMoreHoriz} />
            </IconButton>
        </Flex>
    );

    return (
        <Popover.Root
            positioning={{ placement: 'bottom-end' }}
            open={isOpen}
            onOpenChange={({ open }) => {
                if (!open) onClose();
            }}
            // Keep the portal content out of the DOM until open — one hidden
            // positioner per virtualized row churns document.body on scroll
            // and races React's portal cleanup (see components/Tooltip.tsx).
            lazyMount
            unmountOnExit
        >
            <Popover.Trigger asChild>{trigger}</Popover.Trigger>
            <Portal>
                <Popover.Positioner>
                    <Popover.Content maxWidth='250px'>
                        <Popover.Body padding={0}>
                            {menuItems.map((item, i) => {
                                const { key, node, onClick } = item;
                                const isFirst = i === 0;
                                const isLast = i === menuItems.length - 1;
                                return (
                                    <Button
                                        key={key}
                                        variant='ghost'
                                        size='lg'
                                        color='fg.link'
                                        // One contiguous menu (what the
                                        // remote's `popover` variant used to
                                        // supply): full width, square rows
                                        // separated by a hairline, only the
                                        // outer corners rounded.
                                        width='100%'
                                        h='38px'
                                        px='12px'
                                        fontSize='sm'
                                        borderRadius={0}
                                        borderBottomWidth={isLast ? 0 : '1px'}
                                        borderBottomColor='border'
                                        borderTopRadius={
                                            isFirst ? 5 : undefined
                                        }
                                        borderBottomRadius={
                                            isLast ? 5 : undefined
                                        }
                                        onClick={(e) => {
                                            // Items without onClick bubble to
                                            // the row's handler (navigation);
                                            // items with one stopPropagation.
                                            if (onClick) {
                                                e.stopPropagation();
                                                onClick(e);
                                            }
                                            onClose();
                                        }}
                                    >
                                        <Box
                                            width='100%'
                                            textAlign='left'
                                            p='8px 0'
                                            overflow='hidden'
                                        >
                                            <Text textStyle='body-2'>
                                                {node}
                                            </Text>
                                        </Box>
                                    </Button>
                                );
                            })}
                        </Popover.Body>
                    </Popover.Content>
                </Popover.Positioner>
            </Portal>
        </Popover.Root>
    );
};

const COMPLETE_STATUSES = ['canceled', 'completed', 'errored', 'failed'];

export const JobsRowActionPopover = ({
    job,
    onView,
}: {
    job: { id: string; status: string } & Record<string, unknown>;
    /** Opens the job detail — rows are inert (the name cell carries the
     * link), so the menu item navigates explicitly. */
    onView: () => void;
}) => {
    const jobIsComplete = COMPLETE_STATUSES.includes(job.status);
    const downloadJobFromClickEvent = (e: React.MouseEvent) => {
        e.stopPropagation();
        downloadTextFile(`${job.id}.txt`, JSON.stringify(job), 'text/plain');
    };
    const menuItems: RowActionMenuItem[] = [
        {
            // 'Download job data', not the legacy 'Download complete
            // response': the list payload is the UI projection — the full
            // v0.3 object needs the jobs.get follow-up (schemas.ts).
            key: 'download',
            node: 'Download job data',
            onClick: downloadJobFromClickEvent,
        },
        {
            key: 'view',
            node: 'View job details',
            onClick: (e) => {
                e.stopPropagation();
                onView();
            },
        },
        {
            key: 'copy',
            node: 'Copy job ID to clipboard',
            onClick: (e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(job.id).then(
                    () => toaster.success({ title: 'Copied to clipboard' }),
                    () =>
                        toaster.error({
                            title: 'Could not copy to clipboard',
                        }),
                );
            },
        },
    ];

    return (
        <RowActionPopover
            // Only reachable from the hover icon, which only renders when
            // the job is complete — no completeness re-check needed here.
            onClickEvent={downloadJobFromClickEvent}
            icon={jobIsComplete ? MdOutlineFileDownload : null}
            ariaLabel='Download Job'
            menuItems={menuItems}
        />
    );
};
